/**
 * As três tabelas de etapas — o coração da OS e do PCP GBU.
 *
 * Cada linha aqui vira um cartão no tablet de um setor. É o que o Jeferson
 * marcou em verde: nome da etapa, equipamento, impressões e tempo previsto.
 *
 * As colunas saem do **próprio cabeçalho da tabela**, nunca de posições fixas
 * no código: se uma atualização do sistema da gráfica mexer na largura de uma
 * coluna, o cabeçalho anda junto e a leitura continua certa. Só o significado
 * de cada coluna é conhecido, pelo texto do cabeçalho.
 */

import type { EtapaLida, Secao } from '@pcpgbu/dominio';
import {
  agruparEmLinhas,
  celula,
  colunasDaLinha,
  numeroBr,
  registrosDaTabela,
  segundosDeHhmm,
  type Coluna,
  type Linha,
} from './grade.js';
import type { ItemDeTexto } from './pdf.js';

// Qualquer "Etapas <seção>" é uma seção. Os três títulos da Gráfica Boa União
// têm slug canônico; um título novo (outro ERP) vira o slug do próprio título —
// é o que faz o leitor servir mais de uma empresa.
const RE_CABECALHO = /^Etapas\s+(.+)$/;

const SECAO_POR_TITULO: Record<string, Secao> = {
  'Pré-impressão': 'pre_impressao',
  Impressão: 'impressao',
  Acabamento: 'acabamento',
};

/** "Dobra de capa" → "dobra_de_capa". A seção que a empresa chamar. */
function slugDaSecao(titulo: string): Secao {
  return titulo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Onde as etapas terminam: nas especificações técnicas. */
const RE_FIM = /^Especificações técnicas/;

/** Unidades que aparecem junto da quantidade de impressões (vinil usa metro). */
const RE_QTD_COM_UNIDADE = /^([\d.,]+)\s*(mt|un|fl|kg|lt)?$/;

interface Tabela {
  secao: Secao;
  cabecalho: Linha;
  colunas: Coluna[];
  linhas: Linha[];
}

export function lerEtapas(itens: readonly ItemDeTexto[], avisos: string[]): EtapaLida[] {
  const linhas = agruparEmLinhas(itens);
  const tabelas = acharTabelas(linhas);
  const grade = gradeComum(tabelas);
  const etapas: EtapaLida[] = [];

  for (const tabela of tabelas) {
    const indices = mapearColunas(grade, tabela.secao, avisos);
    const registros = registrosDaTabela(tabela.linhas, grade);

    let sequencia = 0;
    for (const r of registros) {
      const nome = celula(r, indices.nome);
      if (!nome) continue;

      const tempoTexto = indices.tempo >= 0 ? celula(r, indices.tempo) : null;
      const impressoesTexto = indices.impressoes >= 0 ? celula(r, indices.impressoes) : null;

      let impressoes: number | null = null;
      let impressoesUnidade: string | null = null;
      if (impressoesTexto) {
        const m = RE_QTD_COM_UNIDADE.exec(impressoesTexto);
        if (m) {
          impressoes = numeroBr(m[1] ?? null);
          impressoesUnidade = m[2] ?? null;
        } else {
          avisos.push(`Quantidade de impressões não reconhecida: "${impressoesTexto}"`);
        }
      }

      const planoTexto = indices.plano >= 0 ? celula(r, indices.plano) : null;
      const plano = planoTexto && /^\d+$/.test(planoTexto) ? Number(planoTexto) : null;
      if (planoTexto && plano === null) {
        avisos.push(`Coluna "Plano" com texto inesperado: "${planoTexto}"`);
      }

      etapas.push({
        secao: tabela.secao,
        sequencia,
        nome,
        plano,
        equipamento: indices.equipamento >= 0 ? celula(r, indices.equipamento) : null,
        impressoes,
        impressoesUnidade,
        tempoPrevisto: tempoTexto,
        tempoPrevistoSeg: segundosDeHhmm(tempoTexto),
        observacoes: indices.observacoes >= 0 ? celula(r, indices.observacoes) : null,
      });
      sequencia += 1;
    }
  }

  return etapas;
}

/**
 * A grade das três tabelas é uma só — e é preciso uni-las.
 *
 * As três seções são impressas na mesma grade física (nome 12 · Plano 134 ·
 * Equip. 183 · Impressões 276 · Tempo 333 · Observações 367), mas cada uma só
 * imprime o TÍTULO das colunas que costuma usar: o Acabamento não escreve
 * "Impressões" no cabeçalho.
 *
 * Só que às vezes tem valor ali. Na OS 113908, a "Dobra Automática" traz
 * 1.500 em x=276 — a quantidade a dobrar. Lendo o Acabamento pelo cabeçalho
 * dele, esse número cai na coluna do equipamento e a etapa vira "Dobradeira
 * 1.500", com a quantidade perdida dentro do nome da máquina.
 *
 * Então a grade usada é a união dos cabeçalhos das três tabelas. Coluna que a
 * seção não usa simplesmente não recebe célula nenhuma.
 */
function gradeComum(tabelas: readonly Tabela[]): Coluna[] {
  const porX: Coluna[] = [];
  for (const tabela of tabelas) {
    for (const [indice, coluna] of tabela.colunas.entries()) {
      // A primeira célula do cabeçalho é o título da tabela ("Etapas
      // Impressão"), não o nome de uma coluna.
      const nome = indice === 0 ? 'nome' : coluna.nome;
      const existente = porX.find((c) => Math.abs(c.x - coluna.x) <= 2);
      if (!existente) porX.push({ nome, x: coluna.x });
    }
  }
  return porX.sort((a, b) => a.x - b.x);
}

function acharTabelas(linhas: readonly Linha[]): Tabela[] {
  const tabelas: Tabela[] = [];
  let atual: Tabela | null = null;

  for (const linha of linhas) {
    const primeiro = linha.itens[0];
    if (!primeiro) continue;

    const m = RE_CABECALHO.exec(primeiro.texto);
    if (m) {
      const titulo = m[1] ?? '';
      const secao = SECAO_POR_TITULO[titulo] ?? slugDaSecao(titulo);
      atual = { secao, cabecalho: linha, colunas: colunasDaLinha(linha), linhas: [] };
      tabelas.push(atual);
      continue;
    }

    if (RE_FIM.test(primeiro.texto)) {
      atual = null;
      continue;
    }

    if (atual) atual.linhas.push(linha);
  }

  return tabelas;
}

interface IndicesDeColuna {
  nome: number;
  plano: number;
  equipamento: number;
  impressoes: number;
  tempo: number;
  observacoes: number;
}

/**
 * De "Plano", "Equip./Fornecedor", "Impressões", "Tempo", "Observações" para
 * índices. A primeira coluna é sempre o nome da etapa (o próprio título da
 * tabela ocupa a célula do cabeçalho).
 */
function mapearColunas(colunas: readonly Coluna[], secao: Secao, avisos: string[]): IndicesDeColuna {
  const acharPor = (teste: RegExp): number => colunas.findIndex((c) => teste.test(c.nome));

  const indices: IndicesDeColuna = {
    nome: 0,
    plano: acharPor(/^Plano$/),
    equipamento: acharPor(/^Equip\.\/Fornecedor$/),
    impressoes: acharPor(/^Impressões$/),
    tempo: acharPor(/^Tempo$/),
    observacoes: acharPor(/^Observações$/),
  };

  // Estas duas existem em toda OS conhecida. Faltar uma significa que o layout
  // mudou; o aviso leva a OS a ser conferida à mão em vez de entrar torta.
  if (indices.equipamento < 0) {
    avisos.push(`Tabela de ${rotulo(secao)}: não achei a coluna "Equip./Fornecedor".`);
  }
  if (indices.tempo < 0) {
    avisos.push(`Tabela de ${rotulo(secao)}: não achei a coluna "Tempo".`);
  }

  return indices;
}

function rotulo(secao: Secao): string {
  if (secao === 'pre_impressao') return 'pré-impressão';
  if (secao === 'impressao') return 'impressão';
  if (secao === 'acabamento') return 'acabamento';
  return secao.replace(/_/g, ' ');
}
